import { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polygon, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import useVias from '../../hooks/useVias';
import useConfiguracion from '../../hooks/useConfiguracion';
import { obtenerDistrito, puntoEnDistrito, anillosDeDistrito } from '../../utils/distritos';

const TIPO_CALLE = new Set([
  'street', 'road', 'avenue', 'avenue_road', 'boulevard', 'drive',
  'lane', 'way', 'path', 'footway', 'pedestrian', 'steps',
  'service', 'track', 'residential', 'living_street', 'terrace',
  'place', 'square', 'alley', 'passage', 'mews', 'crescent',
  'close', 'court', 'gate', 'grove', 'hill', 'mount', 'parade',
  'promenade', 'rise', 'row', 'vale', 'view', 'walk',
]);

const TIPO_LABEL = {
  'street': 'Calle',
  'road': 'Calle',
  'avenue': 'Av.',
  'avenue_road': 'Av.',
  'boulevard': 'Blvd.',
  'drive': 'Av.',
  'lane': 'Pasaje',
  'way': 'Pasaje',
  'path': 'Sendero',
  'footway': 'Peatonal',
  'pedestrian': 'Peatonal',
  'steps': 'Escaleras',
  'service': 'Servicio',
  'track': 'Pista',
  'residential': 'Residencial',
  'living_street': 'Zona 30',
  'terrace': 'Terraza',
  'place': 'Plaza',
  'square': 'Plaza',
  'alley': 'Callejón',
  'passage': 'Pasaje',
  'mews': 'Pasaje',
  'crescent': 'Media luna',
  'close': 'Callejón',
  'court': 'Callejón',
  'gate': 'Entrada',
  'grove': 'Arboleda',
  'hill': 'Cerro',
  'mount': 'Cerro',
  'parade': 'Paseo',
  'promenade': 'Paseo',
  'rise': 'Subida',
  'row': 'Fila',
  'vale': 'Valle',
  'view': 'Vista',
  'walk': 'Paseo',
};

const getTipoLabel = (type, address) => {
  if (TIPO_CALLE.has(type)) {
    return TIPO_LABEL[type] || type;
  }
  if (address?.road) return 'Calle';
  if (address?.pedestrian) return 'Peatonal';
  if (address?.footway) return 'Peatonal';
  if (address?.path) return 'Sendero';
  if (address?.square || address?.place) return 'Plaza';
  return type;
};

const filtrarYOrdenarCalles = (resultados) => {
  return resultados
    .map(r => {
      const a = r.address || {};
      const tipo = r.type || (a.road ? 'street' : r.type);
      const titulo = r.name || a.road || a.pedestrian || a.footway || a.square || r.display_name || '';
      const zona = [a.neighbourhood, a.suburb]
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i && v !== titulo)
        .join(', ');
      return {
        ...r,
        _tipo: tipo,
        _label: getTipoLabel(tipo, a),
        _titulo: titulo,
        _subtitulo: zona,
      };
    })
    .sort((a, b) => {
      const aEsCalle = TIPO_CALLE.has(a._tipo);
      const bEsCalle = TIPO_CALLE.has(b._tipo);
      if (aEsCalle && !bEsCalle) return -1;
      if (!aEsCalle && bEsCalle) return 1;
      return 0;
    })
    .slice(0, 10);
};

const DELTA_VIEWBOX = 0.35;

// Viewbox (~39 km) desde el centro configurado de la instancia: acota la
// búsqueda a la ciudad (Lima) y evita resultados de todo el mundo.
const construirViewbox = (configuracion) => {
  const lat = Number(configuracion.mapa_lat);
  const lng = Number(configuracion.mapa_lng);
  const left = lng - DELTA_VIEWBOX;
  const right = lng + DELTA_VIEWBOX;
  const top = lat + DELTA_VIEWBOX;
  const bottom = lat - DELTA_VIEWBOX;
  return `${left},${top},${right},${bottom}`;
};

const buscarDirecciones = async (query, viewbox) => {
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=15&addressdetails=1&accept-language=es&countrycodes=pe&viewbox=${viewbox}&bounded=1`;
  const res = await fetch(url);
  const data = await res.json();
  return filtrarYOrdenarCalles(data);
};

const geocodificarInverso = async (lat, lng) => {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=es`;
  const res = await fetch(url);
  const data = await res.json();
  return data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

const CENTRO_DEFECTO = { lat: -12.117939985496957, lng: -77.02892645376073 };

// Side effect to prevent tree-shaking - attach to window at module load
if (typeof window !== 'undefined') {
  window.__GEOCODING_CONSTANTS__ = { TIPO_CALLE, TIPO_LABEL, CENTRO_DEFECTO };
}

const iconoPinArrastrable = new L.DivIcon({
  className: '',
  html: '<div class="mp-pin" style="transform:rotate(-45deg)"></div><div class="pu-sombra-pin"></div>',
  iconSize: [34, 44],
  iconAnchor: [17, 34],
});

const RecentcarMapa = ({ centro }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(centro, 17, { animate: true });
  }, [centro, map]);
  return null;
};

// Permite marcar el punto haciendo clic en el mapa (PC/laptop y móvil),
// además de arrastrar el pin.
const ClickParaMarcar = ({ onPick }) => {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
};

const PasoUbicacion = ({ ubicacion, onCambiar, onContinuar, onVolver }) => {
  const [busqueda, setBusqueda] = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [busquedaSinResultados, setBusquedaSinResultados] = useState(false);
  const [posicion, setPosicion] = useState(
    ubicacion.lat ? { lat: ubicacion.lat, lng: ubicacion.lng } : CENTRO_DEFECTO
  );
  const [precisionMetros, setPrecisionMetros] = useState(null);
  const debounceRef = useRef(null);

  const [modoVia, setModoVia] = useState(false);
  const { vias, loading: buscandoVia, cargarVias } = useVias();
  const { configuracion } = useConfiguracion();
  const debounceViaRef = useRef(null);

  // Restricción opcional: solo registrar dentro del distrito configurado.
  const restringeDistrito = !!configuracion.solo_registrar_en_distrito;
  const [distritoGeo, setDistritoGeo] = useState(null);
  const [fueraDeDistrito, setFueraDeDistrito] = useState(false);

  useEffect(() => {
    if (!ubicacion.direccion) {
      confirmarPosicion(posicion.lat, posicion.lng);
    }
  }, []);

  // Force webpack to keep geocoding functions (prevent tree-shaking)
  useEffect(() => {
    // Dynamic reference prevents tree-shaking - webpack can't statically
    // analyze that this reference is never used
    const refs = {
      buscarDirecciones,
      geocodificarInverso,
      filtrarYOrdenarCalles,
      getTipoLabel,
      TIPO_CALLE,
      TIPO_LABEL,
      CENTRO_DEFECTO
    };
    // eslint-disable-next-line no-unused-vars
    const _forceRefs = Object.keys(refs).length;
  }, []);

  const confirmarPosicion = async (lat, lng, direccionConocida = null) => {
    setPosicion({ lat, lng });
    const direccion = direccionConocida || await geocodificarInverso(lat, lng);
    onCambiar({ ...ubicacion, lat, lng, direccion, viaId: null });
  };

  // Si "solo registrar en el distrito" está activo, carga su polígono y, si el
  // pin inicial quedó fuera, lo lleva al centro del distrito permitido.
  useEffect(() => {
    if (!restringeDistrito) { setDistritoGeo(null); setFueraDeDistrito(false); return undefined; }
    let cancelado = false;
    (async () => {
      const info = await obtenerDistrito(configuracion.distrito_defecto || 'Jesús María');
      if (cancelado || !info?.geojson) return;
      setDistritoGeo(info.geojson);
      if (info.lat && info.lon && !puntoEnDistrito(posicion.lat, posicion.lng, info.geojson)) {
        confirmarPosicion(Number(info.lat), Number(info.lon));
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restringeDistrito, configuracion.distrito_defecto]);

  // Valida en cada movimiento si el pin está dentro del distrito permitido.
  useEffect(() => {
    if (!restringeDistrito || !distritoGeo) { setFueraDeDistrito(false); return; }
    setFueraDeDistrito(!puntoEnDistrito(posicion.lat, posicion.lng, distritoGeo));
  }, [restringeDistrito, distritoGeo, posicion.lat, posicion.lng]);

  const handleBuscarVia = (valor) => {
    setBusqueda(valor);
    clearTimeout(debounceViaRef.current);
    if (!valor.trim()) return;
    debounceViaRef.current = setTimeout(() => cargarVias(valor), 400);
  };

  const elegirVia = (via) => {
    setBusqueda(via.via_chNombre);
    setPrecisionMetros(null);
    const lat = via.via_deLatitud != null ? parseFloat(via.via_deLatitud) : posicion.lat;
    const lng = via.via_deLongitud != null ? parseFloat(via.via_deLongitud) : posicion.lng;
    setPosicion({ lat, lng });
    onCambiar({ ...ubicacion, lat, lng, direccion: via.direccion_completa, viaId: via.id });
  };

  const alternarModoVia = () => {
    setBusqueda('');
    setSugerencias([]);
    setBusquedaSinResultados(false);
    setModoVia((m) => !m);
  };

  const handleBuscar = (valor) => {
    setBusqueda(valor);
    setBusquedaSinResultados(false);
    clearTimeout(debounceRef.current);
    if (!valor.trim()) { setSugerencias([]); return; }
    debounceRef.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const resultados = await buscarDirecciones(valor, construirViewbox(configuracion));
        setSugerencias(resultados);
        setBusquedaSinResultados(resultados.length === 0);
      } finally {
        setBuscando(false);
      }
    }, 400);
  };

  const elegirSugerencia = (resultado) => {
    const etiqueta = resultado._titulo || resultado.display_name;
    setSugerencias([]);
    setBusquedaSinResultados(false);
    setBusqueda(etiqueta);
    setPrecisionMetros(null);
    confirmarPosicion(parseFloat(resultado.lat), parseFloat(resultado.lon), etiqueta);
  };

  const usarMiUbicacion = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      setPrecisionMetros(Math.round(pos.coords.accuracy));
      setBusqueda('');
      setSugerencias([]);
      confirmarPosicion(pos.coords.latitude, pos.coords.longitude);
    });
  };

  const handleDragEnd = (e) => {
    setPrecisionMetros(null);
    const { lat, lng } = e.target.getLatLng();
    confirmarPosicion(lat, lng);
  };

  return (
    <div className="pu-pantalla">
      <MapContainer center={posicion} zoom={17} zoomControl={false} style={{ width: '100%', height: '100%' }}>
        <TileLayer
          attribution="© OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <RecentcarMapa centro={posicion} />
        <ClickParaMarcar onPick={(lat, lng) => { setPrecisionMetros(null); confirmarPosicion(lat, lng); }} />
        {distritoGeo && anillosDeDistrito(distritoGeo).map((anillo, i) => (
          <Polygon
            key={`distrito-zona-${i}`}
            positions={anillo}
            pathOptions={{
              color: fueraDeDistrito ? '#c0392b' : '#1a73e8',
              weight: 2,
              fillColor: fueraDeDistrito ? '#c0392b' : '#1a73e8',
              fillOpacity: 0.06,
            }}
          />
        ))}
        <Marker
          position={posicion}
          icon={iconoPinArrastrable}
          draggable
          eventHandlers={{ dragend: handleDragEnd }}
        />
      </MapContainer>

      <div className="pu-barra-superior">
        <button type="button" className="pu-icon-btn" onClick={onVolver} aria-label="Volver">
          <i className="fas fa-arrow-left"></i>
        </button>
        <input
          type="text"
          className="pu-input-direccion"
          placeholder={modoVia ? 'Buscar vía por nombre…' : 'Buscar dirección…'}
          value={busqueda}
          onChange={(e) => (modoVia ? handleBuscarVia(e.target.value) : handleBuscar(e.target.value))}
        />
      </div>

      <button type="button" className="pu-alternar-modo" onClick={alternarModoVia}>
        <i className={`fas ${modoVia ? 'fa-map-location-dot' : 'fa-road'}`}></i>
        {modoVia ? 'Buscar por dirección en el mapa' : '¿No aparece? Elegir una vía registrada'}
      </button>

      {!modoVia && sugerencias.length === 0 && !buscando && !busquedaSinResultados && (
        <div className="pu-guia-usuario">
          <i className="fas fa-circle-info"></i>
          Busca tu dirección arriba o arrastra el pin del mapa para marcar el lugar exacto
        </div>
      )}

      {modoVia && (
        <div className="pu-sugerencias">
          {buscandoVia && <div className="pu-sugerencia-fila pu-sugerencia-cargando">Buscando…</div>}
          {!buscandoVia && busqueda.trim() && vias.length === 0 && (
            <div className="pu-sugerencia-fila pu-sugerencia-cargando">
              No encontramos "{busqueda}" en el catálogo de vías.
            </div>
          )}
          {!buscandoVia && !busqueda.trim() && (
            <div className="pu-sugerencia-fila pu-sugerencia-cargando">
              Escribe el nombre de la vía (ej. "Arequipa").
            </div>
          )}
          {busqueda.trim() && vias.map((via) => (
            <button key={via.id} type="button" className="pu-sugerencia-fila" onClick={() => elegirVia(via)}>
              {via.direccion_completa}
              <span className="pu-sugerencia-via-tag">{via.tipo_via_display}</span>
            </button>
          ))}
        </div>
      )}

      {!modoVia && (sugerencias.length > 0 || buscando || busquedaSinResultados) && (
        <div className="pu-sugerencias">
          {buscando && <div className="pu-sugerencia-fila pu-sugerencia-cargando">Buscando…</div>}
          {!buscando && busquedaSinResultados && (
            <div className="pu-sugerencia-fila pu-sugerencia-cargando">
              No encontramos "{busqueda}" — prueba con otra dirección o usa tu ubicación.
            </div>
          )}
          {sugerencias.map((s) => (
            <button
              key={s.place_id}
              type="button"
              className="pu-sugerencia-fila"
              onClick={() => elegirSugerencia(s)}
            >
              <span className="pu-sugerencia-tipo-badge">{s._label}</span>
              <span style={{ display: 'flex', flexDirection: 'column', textAlign: 'left', minWidth: 0 }}>
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s._titulo || s.display_name}
                </span>
                {s._subtitulo ? (
                  <span style={{ fontSize: 11, opacity: 0.6 }}>{s._subtitulo}</span>
                ) : null}
              </span>
            </button>
          ))}
          <button type="button" className="pu-sugerencia-fila pu-sugerencia-gps" onClick={usarMiUbicacion}>
            <i className="fas fa-location-crosshairs"></i> Usar mi ubicación actual (GPS)
          </button>
        </div>
      )}

      <div className="pu-tooltip-pin">Arrastra el pin para ajustar</div>

      <div className="pu-hoja-confirmacion">
        <div className="rw-step-header pu-progreso">
          <span className="pu-progress-count">4/5</span>
        </div>
        <div className="pu-confirmacion-linea">
          <span className="pu-punto-verde"></span>
          <strong>Ubicación confirmada</strong>
        </div>
        <p className="pu-direccion-texto">{ubicacion.direccion || 'Ajusta el pin o busca una dirección…'}</p>
        <p className="pu-coordenadas-mono">
          {posicion.lat.toFixed(6)}, {posicion.lng.toFixed(6)}
          {precisionMetros != null && ` ±${precisionMetros} m`}
        </p>
        <input
          type="text"
          className="pu-input-referencia"
          placeholder="Referencia (opcional) — ej. frente al parque"
          value={ubicacion.referencia}
          onChange={(e) => onCambiar({ ...ubicacion, referencia: e.target.value })}
        />
        {restringeDistrito && (
          <p className={`pu-restriccion${fueraDeDistrito ? ' pu-restriccion-error' : ''}`}>
            <i className="fas fa-circle-info"></i>{' '}
            {fueraDeDistrito
              ? `El pin está fuera de ${configuracion.distrito_defecto || 'el distrito'}. Marca dentro de la zona.`
              : `Solo puedes registrar dentro de ${configuracion.distrito_defecto || 'el distrito'}.`}
          </p>
        )}
        <button
          type="button"
          className="rw-boton-primario pu-boton-usar"
          disabled={!ubicacion.direccion || fueraDeDistrito}
          onClick={onContinuar}
        >
          Usar esta ubicación
        </button>
      </div>
    </div>
  );
};

// Force webpack to keep geocoding constants and functions (prevent tree-shaking)
export const _geocodingForced = {
  TIPO_CALLE,
  TIPO_LABEL,
  getTipoLabel,
  filtrarYOrdenarCalles,
  buscarDirecciones,
  geocodificarInverso,
  CENTRO_DEFECTO
};

export default PasoUbicacion;